import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../api-client.js';

// ---------------------------------------------------------------------------
// Free chat — atmosphere, never engine-scored
// ---------------------------------------------------------------------------
// Optional per lobby and off by default. Messages are stamped with the
// author's SEAT LABEL, never their identity — anonymity is governed by
// the mode, and chat must not become the leak. Server-side: account
// required, rate limited, length capped, profanity filtered, reportable.

const MAX_LENGTH = 140;

type ChatMessage = { id: string; seatLabel: string; body: string; at: string };

export function FreeChat({
  matchId,
  canPost,
  /** Bumped by the arena whenever the match stream pushes. */
  refreshKey
}: {
  matchId: string;
  canPost: boolean;
  refreshKey: number;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLOListElement | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = (await api.listChat(matchId)) as ChatMessage[];
      setMessages(rows);
    } catch {
      // A chat outage must never take the arena down with it.
    }
  }, [matchId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages.length]);

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await api.sendChat(matchId, body);
      setDraft('');
      await load();
    } catch (error) {
      setError(error instanceof ApiError ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <ol ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
        {messages.length === 0 && (
          <li className="font-serif text-xs text-ec-bone-dim">No free chat yet.</li>
        )}
        {messages.map((message) => (
          <li key={message.id} className="border-t border-ec-line/40 py-1 first:border-0">
            <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone">
              {message.seatLabel}
            </span>{' '}
            <span className="font-serif text-xs text-ec-face">{message.body}</span>
            <button
              type="button"
              onClick={() => void api.reportChat(message.id, 'inappropriate').catch(() => {})}
              className="ml-1 font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim hover:text-ec-oxblood-2"
              title="Report this message"
            >
              report
            </button>
          </li>
        ))}
      </ol>

      {canPost && (
        <div className="flex shrink-0 gap-1">
          <input
            value={draft}
            maxLength={MAX_LENGTH}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            placeholder="Say something…"
            aria-label="Free chat message"
            /* text-base keeps iOS Safari from auto-zooming on focus. */
            className="min-h-[2.75rem] min-w-0 flex-1 rounded border border-ec-line bg-ec-ground px-2 text-base text-ec-face placeholder:text-ec-bone-dim"
          />
          <button
            type="button"
            disabled={busy || draft.trim().length === 0}
            onClick={() => void send()}
            className="ec-btn min-h-[2.75rem] px-3 text-xs disabled:opacity-40"
          >
            Send
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="shrink-0 font-serif text-[0.65rem] text-ec-oxblood-2">
          {error}
        </p>
      )}
    </div>
  );
}
